import React from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../../../../components/ui/dialog';
import { Button } from '../../../../components/ui/button';
import { formatCurrency, formatDate } from '../utils/formatters';

export const BankSplitDialog = ({
  open,
  onOpenChange,
  bankSplitTarget,
  bankSplitParts,
  setBankSplitParts,
  bankSplitNotes,
  setBankSplitNotes,
  bankSplitLoading,
  handleConfirmBankSplit,
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-2xl" data-testid="bank-split-dialog">
      <DialogHeader>
        <DialogTitle>Split bank transaction</DialogTitle>
        <DialogDescription>
          Break one bank row into multiple parts so each can be individually matched.
          Sum of splits must equal the parent bank amount within R0.01.
        </DialogDescription>
      </DialogHeader>
      {bankSplitTarget && (() => {
        const parentAmt = Number(bankSplitTarget.amount || 0);
        const partsTotal = bankSplitParts.reduce((s, p) => s + (Number(p.amount) || 0), 0);
        const diff = parentAmt - partsTotal;
        const withinTol = Math.abs(diff) <= 0.01;
        const parentSign = parentAmt >= 0 ? 1 : -1;
        const signErr = bankSplitParts.some((p) => {
          const v = Number(p.amount);
          return !isNaN(v) && v !== 0 && ((v > 0 ? 1 : -1) !== parentSign);
        });
        return (
          <div className="space-y-3">
            <div className="rounded border border-gray-200 bg-gray-50 p-3 text-sm">
              <div className="flex justify-between"><span className="text-gray-600">Parent bank amount</span><span className="font-mono font-semibold">{formatCurrency(parentAmt)}</span></div>
              <div className="flex justify-between"><span className="text-gray-600">Description</span><span className="text-gray-800">{bankSplitTarget.description || '-'}</span></div>
              <div className="flex justify-between"><span className="text-gray-600">Date</span><span>{formatDate(bankSplitTarget.posting_date)}</span></div>
            </div>
            <div className="space-y-2 max-h-72 overflow-y-auto">
              {bankSplitParts.map((p, idx) => (
                <div key={idx} className="flex items-center gap-2" data-testid={`bank-split-row-${idx}`}>
                  <input
                    type="number"
                    step="0.01"
                    placeholder={`Amount (must be ${parentSign > 0 ? 'positive' : 'negative'})`}
                    value={p.amount}
                    onChange={(e) => {
                      const copy = [...bankSplitParts];
                      copy[idx] = { ...copy[idx], amount: e.target.value };
                      setBankSplitParts(copy);
                    }}
                    className="w-40 px-2 py-1 text-sm border rounded font-mono"
                    data-testid={`bank-split-amount-${idx}`}
                  />
                  <input
                    type="text"
                    placeholder="Description (optional)"
                    value={p.description}
                    onChange={(e) => {
                      const copy = [...bankSplitParts];
                      copy[idx] = { ...copy[idx], description: e.target.value };
                      setBankSplitParts(copy);
                    }}
                    className="flex-1 px-2 py-1 text-sm border rounded"
                    data-testid={`bank-split-desc-${idx}`}
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 text-xs"
                    onClick={() => {
                      const others = bankSplitParts.reduce((s, p, i) => i === idx ? s : s + (Number(p.amount) || 0), 0);
                      const rest = +(parentAmt - others).toFixed(2);
                      const copy = [...bankSplitParts];
                      copy[idx] = { ...copy[idx], amount: rest.toFixed(2) };
                      setBankSplitParts(copy);
                    }}
                    data-testid={`bank-split-fill-residual-${idx}`}
                  >
                    Fill residual
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 text-xs text-red-600"
                    onClick={() => setBankSplitParts(bankSplitParts.filter((_, i) => i !== idx))}
                    disabled={bankSplitParts.length <= 2}
                    data-testid={`bank-split-remove-${idx}`}
                  >
                    Remove
                  </Button>
                </div>
              ))}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setBankSplitParts([...bankSplitParts, { amount: '', description: '' }])}
              data-testid="bank-split-add-part"
            >
              + Add part
            </Button>
            <div className="border-t pt-3 space-y-1 text-sm">
              <div className="flex justify-between"><span>Split total</span><span className="font-mono">{formatCurrency(partsTotal)}</span></div>
              <div className={`flex justify-between font-medium ${withinTol && !signErr ? 'text-emerald-700' : 'text-red-600'}`}>
                <span>Difference vs parent</span>
                <span className="font-mono" data-testid="bank-split-diff">
                  {formatCurrency(diff)} {withinTol && !signErr ? '✓' : signErr ? '⚠ sign mismatch' : '✗ out of tolerance'}
                </span>
              </div>
            </div>
            <div>
              <label className="text-xs text-gray-600">Audit notes (why this split)</label>
              <textarea
                value={bankSplitNotes}
                onChange={(e) => setBankSplitNotes(e.target.value)}
                placeholder="e.g. Combined payout for 3 separate deals — split for reconciliation"
                className="mt-1 w-full px-2 py-1 text-sm border rounded resize-none h-16"
                data-testid="bank-split-notes"
              />
            </div>
          </div>
        );
      })()}
      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)} disabled={bankSplitLoading}>Cancel</Button>
        <Button
          onClick={handleConfirmBankSplit}
          disabled={bankSplitLoading || !bankSplitTarget}
          data-testid="bank-split-confirm"
        >
          {bankSplitLoading ? 'Splitting…' : 'Split bank transaction'}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);
